import type { ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface Testimonial {
  /** Quote text (≤30 words is ideal). */
  quote: string;
  /** Person's name. */
  name: string;
  /** Role (e.g. "TPO"). */
  role: string;
  /** College or company. */
  org: string;
  /** Photo URL. Falls back to initials on a coloured circle if omitted. */
  photo?: string;
  /**
   * The single hard number that matters most. e.g. "87 drives / season" or
   * "₹9 LPA at Freshworks". This is the strongest trust element.
   */
  number?: string;
  /** Optional element rendered inside the card (e.g. college logo). */
  meta?: ReactNode;
}

export interface TestimonialBandProps {
  quotes: Testimonial[];
  /** Layout style. */
  variant?: 'row' | 'hero';
  className?: string;
}

/**
 * `<TestimonialBand>` — photo + name + role + org + one hard number.
 *
 * Two variants:
 *   - `row`  — 3 cards side-by-side (or stacked on mobile). Best for landing
 *              pages with one quote per audience.
 *   - `hero` — single giant pull-quote with byline. Best for persona pages
 *              or when you have one marquee quote.
 *
 * Rules from docs/design/system/primitives.md:
 *   - Never a carousel with auto-advance.
 *   - Real photos over initials; if you don't have a photo, use initials —
 *     never a stock silhouette.
 *   - The number matters more than the quote copy.
 *   - Hidden entirely if the quotes array is empty (caller can render an
 *     "add a testimonial" empty state if they want).
 */
export function TestimonialBand({ quotes, variant = 'row', className }: TestimonialBandProps) {
  if (quotes.length === 0) return null;

  if (variant === 'hero') {
    const q = quotes[0]!;
    return (
      <figure className={cn('mx-auto max-w-3xl text-center', className)}>
        <blockquote
          className="font-serif italic"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            lineHeight: 1.35,
            color: 'rgb(var(--ink-primary))',
            textWrap: 'pretty' as never,
          }}
        >
          “{q.quote}”
        </blockquote>
        <figcaption className="mt-6 flex items-center justify-center gap-3">
          <Avatar photo={q.photo} name={q.name} />
          <div className="text-left">
            <div className="font-semibold text-sm">{q.name}</div>
            <div className="text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
              {q.role}, {q.org}
            </div>
          </div>
        </figcaption>
        {q.number && (
          <div
            className="mt-4 inline-block px-3 py-1 rounded-full text-sm font-medium"
            style={{
              background: 'rgb(var(--bg-info))',
              color: 'rgb(var(--ink-accent))',
            }}
          >
            {q.number}
          </div>
        )}
      </figure>
    );
  }

  return (
    <div
      className={cn(
        'grid gap-6',
        quotes.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3',
        className,
      )}
    >
      {quotes.slice(0, 3).map((q) => (
        <TestimonialCard key={`${q.name}-${q.org}`} q={q} />
      ))}
    </div>
  );
}

function TestimonialCard({ q }: { q: Testimonial }) {
  return (
    <figure className="card card-pad grid gap-4 h-full">
      <blockquote
        className="text-base"
        style={{ color: 'rgb(var(--ink-primary))', textWrap: 'pretty' as never }}
      >
        “{q.quote}”
      </blockquote>
      <figcaption className="mt-auto flex items-center gap-3">
        <Avatar photo={q.photo} name={q.name} />
        <div>
          <div className="font-semibold text-sm">{q.name}</div>
          <div className="text-sm" style={{ color: 'rgb(var(--ink-tertiary))' }}>
            {q.role}, {q.org}
          </div>
        </div>
      </figcaption>
      {q.number && (
        <div
          className="text-sm font-medium inline-flex items-center gap-1"
          style={{ color: 'rgb(var(--ink-accent))' }}
        >
          {q.number}
        </div>
      )}
      {q.meta}
    </figure>
  );
}

function Avatar({ photo, name }: { photo?: string; name: string }) {
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  }
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className="flex items-center justify-center font-semibold"
      style={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        background: 'rgb(var(--bg-info))',
        color: 'rgb(var(--ink-accent))',
        fontSize: 16,
      }}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}
